import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useDeclarations from 'hooks/useDeclarations';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import { goToDeclarationField } from 'views/declarations/utils/form-utils';
import { resolveIcs2LocationRoute } from './resolveLocationRoute';
import { Ics2ErrorLocation } from './types';

/** Long enough for the destination form to mount before we look for the field in it. */
const FIELD_LOOKUP_DELAY_MS = 100;

/**
 * Takes the reader to the field an error is about, wherever it lives: the right tab, the right
 * house consignment or goods item, scrolled to and flashed.
 *
 * Both error sources funnel through here. A customs rejection resolves its pointer into an
 * `Ics2ErrorLocation`, and the pre-submission parsers build the same shape out of the level
 * indices they already track, so neither has to know how the other addresses the form.
 */
const useGoToIcs2ErrorLocation = (onNavigate?: () => void) => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { declarationId, manifestId, jobId } = useParams();
    const { declaration } = useDeclarations();

    return useCallback(
        (errorLocation?: Ics2ErrorLocation) => {
            if (!errorLocation) return;

            const route = resolveIcs2LocationRoute(errorLocation, declaration?.ics2ENSDeclaration?.payload);

            // The drawer and the modal both cover the form, so whichever one the click came from
            // gets out of the way first - including when the level no longer resolves, so that a
            // click is never silently swallowed behind a panel that stays open.
            onNavigate?.();

            if (route === undefined) return;

            const basePath = getDeclarationUrl({ manifestId, jobId, declarationId });
            const viewOnly = routerLocation.pathname.includes('view-only');

            navigate(`${basePath}${viewOnly ? '/view-only' : ''}${route}`);

            if (errorLocation.fieldPath) goToDeclarationField(errorLocation.fieldPath, FIELD_LOOKUP_DELAY_MS);
        },
        [declaration, manifestId, jobId, declarationId, routerLocation.pathname, navigate, onNavigate]
    );
};

export default useGoToIcs2ErrorLocation;
